export const PARTNER_PREFIX = 'jointPartner::';

export type AdvisorInfoWithWritingCode = Advisor & { bgaWritingCode: string };

export const returnSafeString = (objToConvert: number | null | undefined) =>
  objToConvert?.toString() || '';

export const formatDateForForm = (date: Date): string => {
  if (!date) return '';
  try {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  } catch (error) {
    return '';
  }
};

export const mapMaritalStatus = (maritalStatus: string, carrier: string = 'default'): string => {
  if (!maritalStatus) return 'no';
  const statusLower = maritalStatus.toLowerCase();

  if (
    statusLower.includes('never married') ||
    statusLower.includes('divorced') ||
    statusLower.includes('widowed')
  ) {
    return mapMaritalStatusToCarrier(false, carrier);
  }

  return mapMaritalStatusToCarrier(true, carrier);
};

const mapMaritalStatusToCarrier = (isMarried: boolean, carrier: string): string => {
  switch (carrier) {
    case 'careScout':
      return isMarried ? 'married-legal-partner' : 'single';
    default:
      return isMarried ? 'yes' : 'no';
  }
};

export const mapGender = (gender: string): string => {
  if (!gender) return '';
  const genderLower = gender.toLowerCase();
  if (genderLower.includes('male') && !genderLower.includes('female')) return 'male';
  if (genderLower.includes('female')) return 'female';
  // Note: OneAmerica and CareScout form only supports Male/Female options.
  if (genderLower.includes('intersex')) return ''; // Leave blank for manual review
  return '';
};

export const determineCoupleStatus = (maritalStatus: string): 'yes' | 'no' => {
  if (!maritalStatus) return 'no';
  const statusLower = maritalStatus.toLowerCase();
  
  // Consider as coupled only if married (not partnered)
  if (statusLower === 'married') {
    return 'yes';
  }
  
  return 'no';
};

export const checkMedicalApplianceUsage = (assistiveDevices: string[] | string): boolean => {
  if (!assistiveDevices) return false;

  const deviceList =
    typeof assistiveDevices === 'string'
      ? assistiveDevices.split('|').map(s => s.trim())
      : assistiveDevices;

  if (!Array.isArray(deviceList) || deviceList.length === 0) return false;

  const medicalApplianceTerms = [
    'catheter',
    'oxygen',
    'cpap',
    'bi-pap',
    'defibrillator',
    'pacemaker',
    'dialysis',
    'nebulizer',
    'ventilator',
    'feeding tube',
    'tracheostomy',
    'walker',
    'motorized scooter',
    'stair lift',
    'wheelchair',
    'quad cane',
    'hospital bed',
    'Hoyer lift',
    'respirator',
    'kidney dialysis',
  ];

  return deviceList.some(device =>
    medicalApplianceTerms.some(term => device.toLowerCase().includes(term.toLowerCase())),
  );
};

export const convertHeightToFeetAndInches = (
  heightValue: number | string,
): { feet: number; inches: number } => {
  let totalInches = 0;

  if (typeof heightValue === 'string') {
    // Handle string format like "5'8\"" or "68"
    const match = heightValue.match(/(\d+)'(\d+)"/);
    if (match) {
      const feet = parseInt(match[1], 10);
      const inches = parseInt(match[2], 10);
      totalInches = feet * 12 + inches;
    } else {
      totalInches = parseInt(heightValue, 10) || 0;
    }
  } else {
    totalInches = heightValue || 0;
  }

  if (totalInches <= 0) return { feet: 0, inches: 0 };
  return {
    feet: Math.floor(totalInches / 12),
    inches: totalInches % 12,
  };
};

export const getClientWeightChange = (
  clientWeightGainedLost: string,
  clientWeightGainedLastTwelveMonths: number,
  clientWeightLostLastTwelveMonths: number,
): { weightGained: boolean | undefined; weightChangeAmount: number } => {
  if (clientWeightGainedLost) {
    if (clientWeightGainedLost.toLowerCase().includes('gained')) {
      return {
        weightGained: true,
        weightChangeAmount: clientWeightGainedLastTwelveMonths || 0,
      };
    } else if (clientWeightGainedLost.toLowerCase().includes('lost')) {
      return {
        weightGained: false,
        weightChangeAmount: clientWeightLostLastTwelveMonths || 0,
      };
    }
  }
  return {
    weightGained: undefined,
    weightChangeAmount: 0,
  };
};

export const tobaccoUseOptions = [
  'Cigarettes',
  'Cigars, pipes, or hookahs',
  'Vapes',
  'Chewing tobacco',
  'nicotine patch',
  'nicotine gum',
  'e-cigarette',
];

export const checkForSpecificConditions = (
  diagnosesList: string[] | string,
  conditionKeywords: string[],
): string => {
  if (!diagnosesList || !conditionKeywords || conditionKeywords.length === 0) return 'no';

  const diagnosisArray =
    typeof diagnosesList === 'string'
      ? diagnosesList.split('|').map(s => s.trim().toLowerCase())
      : diagnosesList.map(s => s.trim().toLowerCase());

  return diagnosisArray.some(diagnosis =>
    conditionKeywords.some(keyword => diagnosis.includes(keyword.toLowerCase())),
  )
    ? 'yes'
    : 'no';
};

export const getSubstanceTypes = (
  clientSubstancesUsed: string | string[],
  substanceOptions: string[],
): string => {
  if (!clientSubstancesUsed || clientSubstancesUsed.length === 0) return 'none';

  const substanceIsArray =
    typeof clientSubstancesUsed === 'string'
      ? clientSubstancesUsed.split('|').map(s => s.trim().toLowerCase())
      : clientSubstancesUsed.map(s => s.trim().toLowerCase());

  const usedSubstanceTypes = substanceIsArray.filter(substance =>
    substanceOptions.some(keyword => substance.toLowerCase().includes(keyword.toLowerCase())),
  );

  if (usedSubstanceTypes.length === 0) return 'none';

  return usedSubstanceTypes.join(', ');
};

export const GetAdvisorAddress = (
  advisorAddress: AdvisorAddress | undefined,
  longAddress: boolean = false,
): string => {
  if (!advisorAddress) return '';
  const { apartment, streetAddress, city, state, zipCode } = advisorAddress;
  const shortAddressParts = [apartment, streetAddress].filter(Boolean);
  const longAddressParts = [...shortAddressParts, city, state, zipCode].filter(Boolean);
  return longAddress ? longAddressParts.join(', ') : shortAddressParts.join(', ');
};
